import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiBadge,
  EuiFormRow,
  EuiFieldText,
  EuiTextArea,
} from "@elastic/eui";
import { Subjects, Roles } from "settings/permissions";
import * as ALL_FILTERS from "settings/filters";
import ViewsService from "services/FilteringService/ViewsService";
import {
  getViewsFilter,
  getV2TagFilter,
  isNullOrUndefined,
  isSubjectAuthorizedForUser,
} from "utils";
import { NEGATE_FILTER } from "Consts";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import Button from "components/Buttons/Button";
import Modal from "components/Modal/Modal";
import Switch from "components/Buttons/Switch";
import { handleBooleanFilter } from "../FDFilterDropdownUtils";
import { renderFilterBadgeContent } from "../../TopFilter/TopFilterUtils";
import ViewTemplateSelector from "../components/ViewTemplateSelector";
import "styles/components/filterDropdown/fd_filter_dropdown.css";

const FDViewsDropdown = () => {
  const dispatch = useDispatch();
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [isNew, setNew] = useState<boolean>(false);
  const [isGlobal, setGlobal] = useState<boolean>(false);
  const [isDefault, setDefault] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<any>([]);
  const [filterChips, setFilterChips] = useState<any[]>([]);
  const [viewName, setViewName] = useState<string>("");
  const [viewDescription, setViewDescription] = useState<string>("");
  const [editItem, setEditItem] = useState<any>({});
  const [editFilterObject, setEditFilterObject] = useState<any>({});
  const [editFilterObjectIds, setEditFilterObjectIds] = useState<any>({});

  useEffect(() => {
    if (editItem?.attributes?.isUserDefault) {
      setDefault(true);
    } else {
      setDefault(false);
    }
  }, [editItem]);

  const [filterObject1, setFilterObject1] = useState<any>([]);
  const [viewSnapshot, setViewSnapshot] = useState<any>({});

  const { clientId, GlobalTopFilterObject, GlobalTopFilterObjectValues } =
    useSelector(globalFilterSelector);

  const filterObject = isNew ? GlobalTopFilterObject : editFilterObject;
  const filterObjectIds = isNew ? GlobalTopFilterObjectValues : editFilterObjectIds;

  const canCreateUpdateGlobalView = isSubjectAuthorizedForUser({
    subject: Subjects.User_Settable_Management,
    role: Roles.Admin,
  });
  const canCreateUpdatePrivateView = isSubjectAuthorizedForUser({
    subject: Subjects.User_Settable_Management,
    role: Roles.Write,
  });

  const filterFieldToName: any = {};

  Object.values(ALL_FILTERS).forEach((i) => {
    filterFieldToName[i.field] = i.name;
  });

  const closeModal = () => {
    setViewName("");
    setViewDescription("");
    setGlobal(false);
    setEditItem({});
    setEditFilterObject({});
    setEditFilterObjectIds({});
    setNew(false);
    setModalIsOpen(false);
  };

  const getViews = useCallback(() => {
    setLoading(true);
    ViewsService.getViews({ clientId }).then((response) => {
      setItems(response.filterProfiles);
      setLoading(false);
      if (response.filterProfiles) {
        const defaultView = response.filterProfiles.find(
          (profile: any) => profile.attributes?.isUserDefault
        );
        if (defaultView) {
          setFilterObject1([`${defaultView.id}: ${defaultView.name}`]);
          handleSelectView(defaultView.id);
        }
      }
    });
  }, [clientId]);

  useEffect(() => {
    getViews();
  }, [clientId]);

  const handleCreateView = () => {
    setNew(true);
    setModalIsOpen(true);
  };

  const handleViewKey = (key: string) => {
    //important to keep this mapping and extend if needed
    if (key === "hostName") return "hosts";
    if (key === "instanceName") return "servername";
    if (key === "databaseName") return "databasename";
    if (key === "opportunityStatusTypeId") return "statusTypeId";
    if (key === "waitCategory") return "wait_category";
    if (key === "waitType") return "wait_type";
    if (key === "blockerId") return "blockerid";
    if (key === "dayOfWeek") return "DayOfWeek";
    if (key === "timeOfDay") return "TimeOfDay";
    if (key === "deadlockGroupId") return "deadlockgroupid";
    if (key === "compressionType") return "compressiontype";
    if (key === "tableName") return "table";
    if (key === "jobName") return "job_name";
    if (key === "jobCategory") return "category";
    return key;
  };

  const handleSaveView = async () => {
    setLoading(true);
    const v2Filters = getViewsFilter(filterObject, filterObjectIds);
    const v2TagFilters = getV2TagFilter(filterObject?.tag);

    const payload = {
      name: viewName,
      description: viewDescription,
      filters: {
        ...v2Filters,
        ...(v2Filters.hosts && { hostName: v2Filters.hosts }),
        ...(v2Filters.servername && { instanceName: v2Filters.servername }),
        ...(v2Filters.table && { tableName: v2Filters.table }),
        ...(v2Filters.statusTypeId && { opportunityStatusTypeId: v2Filters.statusTypeId }),
        ...(v2Filters.wait_category && { waitCategory: v2Filters.wait_category }),
        ...(v2Filters.wait_type && { waitType: v2Filters.wait_type }),
        ...(v2Filters.blockerid && { blockerId: v2Filters.blockerid }),
        ...(v2Filters.DayOfWeek && { dayOfWeek: v2Filters.DayOfWeek }),
        ...(v2Filters.TimeOfDay && { timeOfDay: v2Filters.TimeOfDay }),
        ...(v2Filters.deadlockgroupid && { deadlockGroupId: v2Filters.deadlockgroupid }),
        ...(v2Filters.compressiontype && { compressionType: v2Filters.compressiontype }),
        ...(v2Filters.job_name && { jobName: v2Filters.job_name }),
        ...(v2Filters.category && { jobCategory: v2Filters.category }),
        ...(v2Filters.hasSilencingWindow && {
          hasSilencingWindow: handleBooleanFilter(v2Filters.hasSilencingWindow),
        }),
        ...(v2Filters.isUnique && {
          isUnique: handleBooleanFilter(v2Filters.isUnique),
        }),
        ...(v2Filters.isDisabled && {
          isDisabled: handleBooleanFilter(v2Filters.isDisabled),
        }),
        ...(v2Filters.isExpired && {
          isExpired: handleBooleanFilter(v2Filters.isExpired),
        }),
        ...(v2Filters.hasWeakPassword && {
          hasWeakPassword: handleBooleanFilter(v2Filters.hasWeakPassword),
        }),
        ...v2TagFilters,
      },
    };

    if (isNew) {
      const CREATE = isGlobal ? ViewsService.createGlobalView : ViewsService.createView;
      const response = await CREATE({ clientId, payload });

      if (isDefault) {
        await ViewsService.setDefaultView(clientId, response.id);
      }
    } else {
      const UPDATE = editItem?.attributes?.isGlobal
        ? ViewsService.updateGlobalView
        : ViewsService.updateView;
      await UPDATE({ clientId, userFilterProfileId: editItem.id, payload });

      const defaultViewId = isDefault ? editItem.id : null;
      await ViewsService.setDefaultView(clientId, defaultViewId);
    }
    closeModal();
    getViews();
    setLoading(false);
  };

  const handleDeleteView = () => {
    const DELETE = editItem?.attributes?.isGlobal
      ? ViewsService.deleteGlobalView
      : ViewsService.deleteView;

    DELETE({ clientId, userFilterProfileId: editItem.id }).then(() => {
      closeModal();
      getViews();
    });
  };

  const handleValue = (key: any, value: any) => {
    if (key === "tag") {
      return [
        ...(value.includeTags?.length > 0
          ? value.includeTags.map((i: any) => `${i.name}: ${i.value}`) // space from : is important!
          : []),
        ...(value.excludeTags?.length > 0
          ? value.excludeTags.map((i: any) => `${NEGATE_FILTER}${i.name}: ${i.value}`) // space from : is important!
          : []),
      ];
    }

    if (
      ["hasSilencingWindow", "isUnique", "isDisabled", "isExpired", "hasWeakPassword"].includes(key)
    ) {
      return [...(value === true ? ["Yes"] : []), ...(value === false ? ["No"] : [])];
    }

    return [
      ...(value.includeValues ? value.includeValues : []),
      ...(value.excludeValues ? value.excludeValues.map((i: any) => `${NEGATE_FILTER}${i}`) : []),
    ];
  };

  const handleIds = (key: any, value: any) => {
    if (key === "tag") {
      return;
    }

    return [
      ...(value.includeIds ? value.includeIds : []),
      ...(value.excludeIds ? value.excludeIds.map((i: any) => `${NEGATE_FILTER}${i}`) : []),
    ];
  };

  const handlePublishFilters = (filterNames: any, filterIds: any) => {
    setViewSnapshot(filterNames);

    dispatch({
      payload: filterNames,
      type: globalFilterTypes.UPDATE_TOP_FILTER,
    });

    dispatch({
      payload: filterIds,
      type: globalFilterTypes.UPDATE_TOP_FILTER_VALUES,
    });
  };

  const handleView = (data: any) => {
    const filterList = Object.entries(data).filter(([_, value]) => !isNullOrUndefined(value));

    const filterNames = filterList.map(([key, value]) => {
      return {
        [handleViewKey(key)]: handleValue(key, value),
      };
    });

    const filterIds = filterList.map(([key, value]) => {
      return {
        [handleViewKey(key)]: handleIds(key, value),
      };
    });

    let names = {};
    filterNames.forEach((i) => {
      names = {
        ...names,
        ...i,
      };
    });

    let ids = {};
    filterIds.forEach((i) => {
      ids = {
        ...ids,
        ...i,
      };
    });

    return [names, ids];
  };

  const handleSelectView = (id: any) => {
    ViewsService.getView({ clientId, userFilterProfileId: id }).then((response) => {
      const data = response.filter || {};
      const [filterNames, filterIds] = handleView(data);
      handlePublishFilters(filterNames, filterIds);

      const selectedView = items.find((item: any) => item.id === id);
      if (selectedView) {
        setFilterObject1([`${id}: ${selectedView.name}`]);
      }
    });
  };

  const handleEditView = (userFilterProfileId: any) => {
    setLoading(true);
    ViewsService.getView({ clientId, userFilterProfileId }).then((response) => {
      const data = response.filter || {};
      const [filterNames, filterIds] = handleView(data);
      setViewDescription(response.description);
      setEditFilterObject(filterNames);
      setEditFilterObjectIds(filterIds);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (filterObject1?.length > 0) {
      const a = filterObject1?.[0];
      const [id, name] = a.split(":");
      handleSelectView(id);
    }
  }, [filterObject1]);

  useEffect(() => {
    const isEmpty: any = Object.values(GlobalTopFilterObject)?.every((i: any) => i?.length === 0);
    const isNonSame =
      Object.keys(viewSnapshot)?.length > 0 &&
      JSON.stringify(GlobalTopFilterObject) !== JSON.stringify(viewSnapshot);

    if (isEmpty || isNonSame) {
      setFilterObject1([]);
    }
  }, [GlobalTopFilterObject, viewSnapshot]);

  useEffect(
    () => () => {
      setItems([]);
      setLoading(false);
    },
    []
  );

  const removeFilterChip = (chip: any) => {
    if (isNew) {
      const chipIdx = GlobalTopFilterObject[chip.field]?.findIndex((c: any) => c === chip?.name);
      const values = GlobalTopFilterObjectValues ? GlobalTopFilterObjectValues[chip.field] : [];

      dispatch({
        payload: {
          [chip.field]: values?.filter((i: any, idx: number) => idx !== chipIdx),
        },
        type: globalFilterTypes.UPDATE_TOP_FILTER_VALUES,
      });

      dispatch({
        payload: {
          [chip.field]: GlobalTopFilterObject[chip.field].filter((i: any) => i !== chip.name),
        },
        type: globalFilterTypes.UPDATE_TOP_FILTER,
      });
    } else {
      const chipIdx = filterObject[chip.field]?.findIndex((c: any) => c === chip?.name);

      setEditFilterObject({
        ...filterObject,
        [chip.field]: filterObject[chip.field].filter((i: any) => i !== chip.name),
      });

      setEditFilterObjectIds({
        ...filterObjectIds,
        [chip.field]: filterObjectIds[chip.field].filter((i: any, idx: number) => idx !== chipIdx),
      });
    }
  };

  const handleChips = useCallback(() => {
    if (filterObject) {
      const selectedFilters: any[] = [];
      //const filterFields = (filterList || []).map((f) => f.field);

      Object.entries(filterObject).forEach(([key, val]: any) => {
        // const keyExists = filterFields.includes(key);
        // edit and see chips only possible on the page where it was created
        // because filter fieldName does not exist on GlobalFilterObject

        if (val) {
          if (val?.length > 0) {
            const uniq: any = new Set(val);
            const chips: any[] = [...uniq].map((i) => ({
              field: key,
              filterName: filterFieldToName[key], //filterList.find((i) => i.field === key)?.name,
              name: `${i}`,
            }));
            selectedFilters.push(...chips);
          }
        }
      });

      setFilterChips(selectedFilters);
    }
  }, [filterObject]);

  useEffect(() => {
    handleChips();
  }, [modalIsOpen, filterObject]);

  return (
    <>
      <ViewTemplateSelector
        Items={[
          {
            id: 1,
            display: "My Views",
            name: "My Views",
            value: "My Views",
            type: "local",
            values:
              items
                ?.filter((i: any) => !i?.attributes?.isGlobal)
                ?.map((i: any) => ({
                  ...i,
                  name: i.type,
                  value: i.name,
                })) || [],
          },
          {
            id: 2,
            display: "Global Views",
            name: "Global Views",
            value: "Global Views",
            type: "global",
            values:
              items
                ?.filter((i: any) => i?.attributes?.isGlobal)
                ?.map((i: any) => ({
                  ...i,
                  name: i.type,
                  value: i.name,
                })) || [],
          },
        ]}
        Name={"Views"}
        Field={"views"}
        filterObject={filterObject1}
        setFilterObject={setFilterObject1}
        singleSelection
        disableSorting
        onCreate={handleCreateView}
        onEdit={(option: any) => {
          handleEditView(option.id);
          setViewName(option.name);
          setEditItem(option);
          setModalIsOpen(true);
        }}
        onCancel={() => {
          setViewSnapshot({});
          // setFilterObject1([]);
        }}
        canCreateUpdateGlobalViews={canCreateUpdateGlobalView}
        canCreateUpdatePrivateViews={canCreateUpdatePrivateView}
      />
      {modalIsOpen && (
        <Modal
          title={isNew ? "Save Current View" : "Edit View"}
          onClose={closeModal}
          maxWidth={600}
          children={
            <EuiFlexGroup gutterSize="m" direction="column">
              <EuiFlexGroup
                justifyContent="flexEnd"
                responsive={false}
                style={{ marginTop: "12px" }}>
                {isNew && canCreateUpdateGlobalView && (
                  <EuiFlexItem grow={false}>
                    <EuiFormRow label={""}>
                      <Switch
                        label={"Set as Global View"}
                        checked={isGlobal}
                        onChange={() => {
                          setGlobal(!isGlobal);
                        }}
                      />
                    </EuiFormRow>
                  </EuiFlexItem>
                )}

                <EuiFlexItem grow={false}>
                  <EuiFormRow label={""}>
                    <Switch
                      label={"Set as Default View"}
                      checked={isDefault}
                      onChange={() => {
                        setDefault(!isDefault);
                      }}
                    />
                  </EuiFormRow>
                </EuiFlexItem>
              </EuiFlexGroup>

              <EuiFlexGroup direction="column" alignItems="center" responsive={false}>
                <EuiFlexItem grow={1}>
                  <EuiFormRow
                    label={"View Name"}
                    fullWidth
                    style={{ width: "550px" }}
                    helpText={viewName?.length === 50 ? "50 characters max" : ""}>
                    <EuiFieldText
                      id={"view-name"}
                      onChange={(e: any) => {
                        setViewName(e.target.value);
                      }}
                      maxLength={50}
                      value={viewName}
                      placeholder={isNew ? "View Name" : "Current Edit Name"}
                      fullWidth
                    />
                  </EuiFormRow>
                </EuiFlexItem>

                <EuiFlexItem grow={1}>
                  <EuiFormRow
                    className={isGlobal || editItem?.attributes?.isGlobal ? "row-required" : ""}
                    label={"View Description"}
                    fullWidth
                    style={{ width: "550px" }}
                    helpText={viewDescription?.length === 200 ? "200 characters max" : ""}>
                    <EuiTextArea
                      id={"view-description"}
                      onChange={(e) => {
                        setViewDescription(e.target.value);
                      }}
                      value={viewDescription}
                      placeholder={"View Description"}
                      fullWidth
                      rows={3}
                      maxLength={200}
                    />
                  </EuiFormRow>
                </EuiFlexItem>

                {filterChips.length > 0 && (
                  <EuiFlexItem grow={false}>
                    <EuiFormRow label={"Filters and Tags"} fullWidth style={{ width: "550px" }}>
                      <EuiFlexGroup className="chips-group" wrap style={{ paddingTop: "2px" }}>
                        {filterChips.map((chip) => {
                          return (
                            <EuiFlexItem grow={false}>
                              <EuiBadge
                                className="chips-badge"
                                color="white"
                                iconOnClick={() => {
                                  removeFilterChip(chip);
                                }}
                                iconOnClickAriaLabel="chips-badge"
                                iconSide="right"
                                iconType="cross"
                                key={chip.name}>
                                {renderFilterBadgeContent(chip)}
                              </EuiBadge>
                            </EuiFlexItem>
                          );
                        })}
                      </EuiFlexGroup>
                    </EuiFormRow>
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
            </EuiFlexGroup>
          }
          footer={
            <EuiFlexGroup gutterSize="m" justifyContent="flexEnd">
              {!isNew && editItem?.modificationRules?.canDelete && (
                <EuiFlexItem grow={false}>
                  <Button type="empty" text={"Delete"} onClick={handleDeleteView} />
                </EuiFlexItem>
              )}
              <EuiFlexItem grow={false}>
                <Button type="secondary" text={"Cancel"} onClick={closeModal} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <Button
                  isLoading={loading}
                  type="primary"
                  text={"Save"}
                  onClick={handleSaveView}
                  disabled={
                    isGlobal || editItem?.attributes?.isGlobal
                      ? viewName?.length === 0 || viewDescription?.length === 0
                      : viewName?.length === 0
                  }
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          }
        />
      )}
    </>
  );
};

export default FDViewsDropdown;
